import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, Bot, User, FolderOpen, CheckSquare, Loader2,
  ArrowLeft, BarChart3, Trash2, RotateCcw, ChevronDown, ChevronRight,
  Plus, Zap
} from 'lucide-react';
import { useAI } from '../../../context/AIContext';

// ── Design tokens — mirrors Dashboard.tsx ─────────────────────────────────────
const T = {
  page:         'var(--lyra-color-surface)',
  surface:      'var(--lyra-color-surface)',
  surface2:     'var(--lyra-color-surface-2)',
  surface3:     'var(--app-surface-3)',
  sidebar:      'var(--lyra-color-surface-2)',
  border:       'var(--lyra-color-border)',
  border2:      'var(--app-border-strong)',
  ink:          'var(--lyra-color-text)',
  ink2:         'var(--app-text-2)',
  ink3:         'var(--lyra-color-text-muted)',
  ink4:         'var(--app-text-3)',
  accent:       'var(--lyra-color-primary)',
  accentLight:  'var(--app-primary-soft)',
  accentDark:   'var(--app-primary-deep)',
  accentMid:    'var(--app-primary-soft-2)',
  green:        'var(--lyra-color-success)',
  greenBg:      'var(--lyra-color-success-bg)',
  greenBorder:  'var(--app-success-border)',
  amber:        'var(--lyra-color-warning)',
  amberBg:      'var(--lyra-color-warning-bg)',
  red:          'var(--lyra-color-danger)',
  redBg:        'var(--lyra-color-danger-bg)',
  blue:         'var(--lyra-color-primary)',
  violet:       'var(--lyra-color-primary)',
};

// ── API helpers ───────────────────────────────────────────────────────────────
const API_BASE = () =>
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL || 'https://findashback.onrender.com'
    : process.env.REACT_APP_API_URL || 'http://localhost:5001';

function authHeaders() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function chatWithPMAgent(prompt: string, history: Message[], modelId?: string) {
  const res = await fetch(`${API_BASE()}/api/pm-agent/chat`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ prompt, history, modelId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Agent request failed');
  return data;
}

async function fetchSnapshot() {
  const res = await fetch(`${API_BASE()}/api/pm-agent/snapshot`, {
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Snapshot failed');
  return data;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface Message {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: any[];
  modelName?: string;
  durationMs?: number;
  isLoading?: boolean;
}

interface Project {
  _id: string;
  title: string;
  status: string;
  priority: string;
  progress: number;
  taskCount: number;
  completedTaskCount: number;
}

interface Stats {
  total: number;
  todo: number;
  inProgress: number;
  done: number;
  overdue: number;
  completionRate: number;
}

// ── Suggestion chips ──────────────────────────────────────────────────────────
const SUGGESTIONS = [
  "Show me all my projects",
  "Create a project for building a portfolio website with tasks",
  "What tasks are currently in progress?",
  "Show task stats",
  "Create 3 tasks for my most recent project",
  "Mark my overdue tasks as high priority",
];

const PROJECT_AGENT_ROUTE = '/tools/project-management/project-agent';

// ── Lightweight markdown → JSX (no external deps) ────────────────────────────
function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  const inlineFormat = (str: string): React.ReactNode => {
    const parts = str.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((p, idx) => {
      if (p.startsWith('**') && p.endsWith('**'))
        return <strong key={idx} style={{ fontWeight: 600, color: T.ink }}>{p.slice(2, -2)}</strong>;
      if (p.startsWith('`') && p.endsWith('`'))
        return <code key={idx} style={{ background: T.surface3, color: T.accent, padding: '1px 5px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>{p.slice(1, -1)}</code>;
      return p;
    });
  };

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.startsWith('```')) {
      const block: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) { block.push(lines[i]); i++; }
      elements.push(
        <pre key={i} style={{ background: T.surface3, borderRadius: 8, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', color: T.accent, overflowX: 'auto', margin: '6px 0', whiteSpace: 'pre-wrap', border: `1px solid ${T.border}` }}>
          {block.join('\n')}
        </pre>
      );
      i++; continue;
    }

    // Heading
    if (/^#{1,3}\s/.test(line)) {
      const level = line.match(/^(#+)/)?.[1].length || 1;
      const txt = line.replace(/^#+\s/, '');
      const style: React.CSSProperties = {
        fontWeight: 700,
        color: T.ink,
        margin: level === 1 ? '10px 0 4px' : '8px 0 3px',
        fontSize: level === 1 ? 15 : level === 2 ? 13 : 12,
      };
      elements.push(<p key={i} style={style}>{inlineFormat(txt)}</p>);
      i++; continue;
    }

    // Bullet list
    if (/^[-*•]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*•]\s/, ''));
        i++;
      }
      elements.push(
        <ul key={i} style={{ paddingLeft: 18, margin: '4px 0', color: T.ink2 }}>
          {items.map((it, j) => <li key={j} style={{ fontSize: 13, marginBottom: 2 }}>{inlineFormat(it)}</li>)}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={i} style={{ paddingLeft: 20, margin: '4px 0', color: T.ink2 }}>
          {items.map((it, j) => <li key={j} style={{ fontSize: 13, marginBottom: 2 }}>{inlineFormat(it)}</li>)}
        </ol>
      );
      continue;
    }

    // Table
    if (line.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!/^\|[-|:\s]+\|$/.test(lines[i]))
          rows.push(lines[i].split('|').filter(Boolean).map(c => c.trim()));
        i++;
      }
      if (rows.length) {
        elements.push(
          <div key={i} style={{ overflowX: 'auto', margin: '8px 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>{rows[0].map((h, j) => (
                  <th key={j} style={{ border: `1px solid ${T.border2}`, padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: T.ink, background: T.surface2 }}>{inlineFormat(h)}</th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.slice(1).map((r, ri) => (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? T.surface : T.surface2 }}>
                    {r.map((c, ci) => <td key={ci} style={{ border: `1px solid ${T.border}`, padding: '4px 8px', color: T.ink2 }}>{inlineFormat(c)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // HR
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={i} style={{ border: 'none', borderTop: `1px solid ${T.border}`, margin: '8px 0' }}/>);
      i++; continue;
    }

    // Empty line
    if (!line.trim()) { elements.push(<div key={i} style={{ height: 4 }}/>); i++; continue; }

    // Paragraph
    elements.push(<p key={i} style={{ fontSize: 13, color: T.ink2, lineHeight: 1.6, margin: '2px 0' }}>{inlineFormat(line)}</p>);
    i++;
  }

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{elements}</div>;
}

// ── Tool call pill ─────────────────────────────────────────────────────────────
function ToolCallPill({ call }: { call: any }) {
  const [open, setOpen] = useState(false);
  const skill = call.skillId || 'tool';
  const ok    = call.ok;
  return (
    <div style={{ marginTop: 4, fontSize: 11 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 8px', borderRadius: 999,
          border: `1px solid ${ok ? T.greenBorder : 'var(--app-danger-border)'}`,
          background: ok ? T.greenBg : T.redBg,
          color: ok ? T.green : T.red,
          fontFamily: 'monospace', fontSize: 11, cursor: 'pointer',
        }}
      >
        {ok ? '✓' : '✗'} {skill}
        {open ? <ChevronDown size={9}/> : <ChevronRight size={9}/>}
      </button>
      {open && (
        <pre style={{ marginTop: 4, marginLeft: 8, padding: 8, borderRadius: 6, background: T.surface3, fontSize: 13, color: T.ink3, overflowX: 'auto', maxHeight: 140, whiteSpace: 'pre-wrap', border: `1px solid ${T.border}` }}>
          {JSON.stringify(ok ? call.result : call.error, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ── Project sidebar card ──────────────────────────────────────────────────────
function ProjectCard({ p }: { p: Project }) {
  const statusColor: Record<string, string> = {
    Planning:  T.blue,
    Active:    T.green,
    'On Hold': T.amber,
    Completed: T.violet,
    Cancelled: T.red,
  };
  return (
    <div style={{ padding: '8px 10px', borderRadius: 8, background: T.surface, border: `1px solid ${T.border}`, transition: 'border-color .15s' }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = T.border2)}
      onMouseLeave={e => (e.currentTarget.style.borderColor = T.border)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{p.title}</span>
        <span style={{ fontSize: 13, color: statusColor[p.status] || T.ink3, flexShrink: 0 }}>{p.status}</span>
      </div>
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, height: 3, borderRadius: 99, background: T.surface3, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 99, background: T.accent, width: `${p.progress}%`, transition: 'width .3s' }}/>
        </div>
        <span style={{ fontSize: 13, color: T.ink4, flexShrink: 0 }}>{p.completedTaskCount}/{p.taskCount}</span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const PMAgentPage: React.FC = () => {
  const navigate    = useNavigate();
  const bottomRef   = useRef<HTMLDivElement>(null);
  const inputRef    = useRef<HTMLTextAreaElement>(null);

  const { getEnabledModels, selectedModelId: ctxModelId } = useAI();
  const enabledModels = getEnabledModels();

  const [messages,        setMessages]        = useState<Message[]>([]);
  const [input,           setInput]           = useState('');
  const [loading,         setLoading]         = useState(false);
  const [snapshot,        setSnapshot]        = useState<{ projects: Project[]; stats: Stats } | null>(null);
  const [snapshotTs,      setSnapshotTs]      = useState(0);
  const [localModelId,    setLocalModelId]    = useState<string | null>(null);

  // Effective model: local override → context selection → auto
  const effectiveModelId = localModelId ?? ctxModelId ?? null;

  const loadSnapshot = useCallback(async () => {
    try {
      const data = await fetchSnapshot();
      setSnapshot(data);
      setSnapshotTs(Date.now());
    } catch (_) {}
  }, []);

  useEffect(() => { loadSnapshot(); }, [loadSnapshot]);

  const refreshSnapshotSoon = () => setTimeout(loadSnapshot, 800);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text: string) => {
    const prompt = text.trim();
    if (!prompt || loading) return;

    const history: Message[] = messages
      .filter(m => !m.isLoading)
      .map(m => ({ role: m.role, content: m.content }));

    setMessages(prev => [
      ...prev,
      { role: 'user', content: prompt },
      { role: 'assistant', content: '', isLoading: true },
    ]);
    setInput('');
    setLoading(true);

    try {
      const res = await chatWithPMAgent(prompt, history, effectiveModelId || undefined);
      setMessages(prev => [
        ...prev.slice(0, -1),
        {
          role:       'assistant',
          content:    res.result || '(No response)',
          toolCalls:  res.toolCalls || [],
          modelName:  res.modelName,
          durationMs: res.durationMs,
        },
      ]);
      refreshSnapshotSoon();
    } catch (err: any) {
      setMessages(prev => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: `Error: ${err.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  const clearChat = () => setMessages([]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100%', minHeight: '100%', background: T.page, overflow: 'hidden', fontFamily: "'DM Sans', system-ui, sans-serif", color: T.ink }}>

      {/* ── Sidebar ── */}
      <aside style={{ width: 256, flexShrink: 0, display: 'flex', flexDirection: 'column', background: T.sidebar, borderRight: `1px solid ${T.border}` }}>

        {/* Sidebar header */}
        <div style={{ padding: '16px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 600, color: T.ink }}>Project Agent</div>
              <div style={{ fontSize: 13, color: T.ink4 }}>AI project management workspace</div>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        {snapshot?.stats && (
          <div style={{ padding: '12px', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.07em', color: T.ink4, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              <BarChart3 size={10} color={T.ink4}/> Overview
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {[
                { label: 'Projects',    value: snapshot.projects?.length ?? 0, color: T.ink },
                { label: 'Tasks',       value: snapshot.stats.total,            color: T.ink },
                { label: 'In Progress', value: snapshot.stats.inProgress,       color: T.amber },
                { label: 'Done',        value: snapshot.stats.done,             color: T.green },
                { label: 'Overdue',     value: snapshot.stats.overdue,          color: T.red },
                { label: 'Complete',    value: `${snapshot.stats.completionRate}%`, color: T.accent },
              ].map(s => (
                <div key={s.label} style={{ borderRadius: 8, background: T.surface, border: `1px solid ${T.border}`, padding: '6px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 9, color: T.ink4, marginTop: 1 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Projects list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.07em', color: T.ink4, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <FolderOpen size={10} color={T.ink4}/> Projects
            </span>
            <button
              onClick={loadSnapshot}
              title="Refresh"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.ink4, display: 'flex', padding: 0 }}
              onMouseEnter={e => (e.currentTarget.style.color = T.ink2)}
              onMouseLeave={e => (e.currentTarget.style.color = T.ink4)}
            >
              <RotateCcw size={10}/>
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {snapshot?.projects?.length
              ? snapshot.projects.map(p => <ProjectCard key={p._id} p={p}/>)
              : <p style={{ fontSize: 11, color: T.ink4, textAlign: 'center', padding: '16px 0' }}>No projects yet</p>
            }
          </div>
        </div>
      </aside>

      {/* ── Main chat area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Chat header */}
        <div style={{ flexShrink: 0, padding: '12px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.surface }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={14} color={T.accent}/>
            <span style={{ fontSize: 20, fontWeight: 600, color: T.ink }}>Project Agent Chat</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Model picker */}
            <select
              value={effectiveModelId || 'auto'}
              onChange={e => setLocalModelId(e.target.value === 'auto' ? null : e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 7, border: `1px solid ${T.border}`, fontSize: 12, color: T.ink3, background: T.surface2, cursor: 'pointer' }}
            >
              <option value="auto">⚡ Auto (from AI Console)</option>
              {enabledModels.map((m: any) => (
                <option key={m.id} value={m.id}>{m.name || m.id}{m.badge ? ` · ${m.badge}` : ''}</option>
              ))}
            </select>
            <button
              onClick={clearChat}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: T.ink4, background: 'none', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.color = T.red)}
              onMouseLeave={e => (e.currentTarget.style.color = T.ink4)}
            >
              <Trash2 size={12}/> Clear
            </button>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Welcome state */}
          {messages.length === 0 && (
            <div style={{ maxWidth: 560, margin: '40px auto 0', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: 18, background: T.accentLight, border: `1px solid ${T.accentMid}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Bot size={28} color={T.accent}/>
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: T.ink, margin: '0 0 8px' }}>Project Agent</h1>
              <p style={{ fontSize: 13, color: T.ink3, margin: '0 0 24px', lineHeight: 1.6 }}>
                Your AI project manager. Create projects, break down tasks, assign teams,
                and track progress — all through natural conversation.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {SUGGESTIONS.map(s => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, fontSize: 12, color: T.ink3, cursor: 'pointer', textAlign: 'left', transition: 'all .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.accentMid; e.currentTarget.style.background = T.accentLight; e.currentTarget.style.color = T.accent; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.surface; e.currentTarget.style.color = T.ink3; }}
                  >
                    <Plus size={11} color={T.accent} style={{ flexShrink: 0 }}/> {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message bubbles */}
          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, maxWidth: 720, alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>

              {/* Avatar */}
              <div style={{
                flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: msg.role === 'user' ? T.accent : T.surface2,
                border: `1px solid ${msg.role === 'user' ? T.accentDark : T.border}`,
              }}>
                {msg.role === 'user'
                  ? <User size={13} color="var(--app-on-accent)"/>
                  : <Bot size={13} color={T.accent}/>
                }
              </div>

              {/* Bubble + meta */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '85%', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: 16, fontSize: 13, lineHeight: 1.55,
                  borderTopRightRadius: msg.role === 'user' ? 4 : 16,
                  borderTopLeftRadius:  msg.role === 'user' ? 16 : 4,
                  background: msg.role === 'user' ? T.accent : T.surface,
                  border: `1px solid ${msg.role === 'user' ? T.accentDark : T.border}`,
                  color: msg.role === 'user' ? 'var(--lyra-color-surface)' : T.ink,
                  boxShadow: '0 1px 3px rgba(0,0,0,.05)',
                }}>
                  {msg.isLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.ink3 }}>
                      <Loader2 size={14} color={T.accent} style={{ animation: 'spin 1s linear infinite' }}/>
                      <span style={{ fontSize: 13 }}>Thinking…</span>
                    </div>
                  ) : msg.role === 'assistant' ? (
                    <SimpleMarkdown text={msg.content}/>
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
                  )}
                </div>

                {/* Tool call pills */}
                {msg.toolCalls && msg.toolCalls.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 4 }}>
                    {msg.toolCalls.map((tc, j) => <ToolCallPill key={j} call={tc}/>)}
                  </div>
                )}

                {/* Meta */}
                {msg.modelName && (
                  <span style={{ fontSize: 13, color: T.ink4, paddingLeft: 4 }}>
                    {msg.modelName} · {msg.durationMs ? `${(msg.durationMs / 1000).toFixed(1)}s` : ''}
                  </span>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef}/>
        </div>

        {/* Input area */}
        <div style={{ flexShrink: 0, padding: '12px 24px 20px', borderTop: `1px solid ${T.border}`, background: T.surface }}>
          <div
            style={{ display: 'flex', alignItems: 'flex-end', gap: 10, background: T.page, border: `1.5px solid ${T.border}`, borderRadius: 14, padding: '8px 12px', transition: 'border-color .15s' }}
            onFocus={e => (e.currentTarget.style.borderColor = T.accent)}
            onBlur={e => (e.currentTarget.style.borderColor = T.border)}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={handleKeyDown}
              placeholder="Ask Lyra PM anything… e.g. 'Create a project for my e-commerce app with 5 tasks'"
              rows={1}
              disabled={loading}
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', resize: 'none', fontSize: 13, color: T.ink, lineHeight: 1.5, minHeight: 24, maxHeight: 120, fontFamily: 'inherit' }}
            />
            <button
              onClick={() => send(input)}
              disabled={loading || !input.trim()}
              style={{
                flexShrink: 0, width: 32, height: 32, borderRadius: 10,
                background: loading || !input.trim() ? T.surface3 : T.accent,
                border: 'none', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background .15s',
              }}
              onMouseEnter={e => { if (input.trim() && !loading) e.currentTarget.style.background = T.accentDark; }}
              onMouseLeave={e => { if (input.trim() && !loading) e.currentTarget.style.background = T.accent; }}
            >
              {loading
                ? <Loader2 size={14} color={T.ink3} style={{ animation: 'spin 1s linear infinite' }}/>
                : <Send size={14} color={loading || !input.trim() ? T.ink4 : 'var(--lyra-color-surface)'}/>
              }
            </button>
          </div>
          <p style={{ fontSize: 13, color: T.ink4, marginTop: 6, textAlign: 'center' }}>
            Enter to send · Shift+Enter for new line · Lyra PM can create, update, and delete projects &amp; tasks
          </p>
        </div>
      </div>
    </div>
  );
};

export default PMAgentPage;
